import { BoxProps } from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";

import { makeUnique } from "@nens/shared-utils";

type UseDragProps = {
  // Data sent with the drag event
  ids: string[];
  // drag ID to search for dragging state
  dragId: string;
  // On drop event listener
  onDropInto?: (ids: string[]) => void;
};

export function useDrag({ ids, dragId, onDropInto }: UseDragProps) {
  const intl = useIntl();
  const dragPreviewRef = useRef<HTMLDivElement | null>(null);
  const [droppableHover, setDroppableHover] = useState(false);

  const [isDraggingThisEl, setIsDraggingThisEl] = useState(false);
  const [isDraggingAnEl, setIsDraggingAnEl] = useState(false);

  const dropProps: BoxProps = onDropInto
    ? {
        onDragLeave: (e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            setDroppableHover(false);
          }
        },
        onDragEnter: () => {
          setDroppableHover(true);
        },
        onDrop: (e) => {
          e.preventDefault();
          setDroppableHover(false);
          const receivedIds = JSON.parse(e.dataTransfer.getData("text/plain"));
          if (onDropInto) onDropInto(receivedIds);
        },
        ...(droppableHover
          ? {
              bg: "brand.light_sky !important",
              sx: { "*": { pointerEvents: "none" } },
            }
          : {}),
      }
    : {};

  const dragProps: BoxProps & { "data-drag-id": string } = {
    "data-drag-id": dragId,
    draggable: true,
    onDragStart: (e) => {
      setIsDraggingThisEl(true);
      e.dataTransfer.setData("text/plain", JSON.stringify(ids));
      if (dragPreviewRef.current && ids.length > 1) {
        dragPreviewRef.current.innerText = intl.formatMessage(
          {
            id: "row.dragging.items",
            defaultMessage: "Dragging {amount} items",
          },
          { amount: makeUnique(ids).length }
        );
        if (dragPreviewRef.current) e.dataTransfer.setDragImage(dragPreviewRef.current, 0, 0);
      }
    },
  };

  // Create custom-drag-preview showing amount of items being dragged
  useEffect(() => {
    const elementId = "custom-drag-preview";
    let preview = document.getElementById(elementId) as HTMLDivElement | null;

    if (!preview) {
      preview = document.createElement("div");
      preview.id = elementId;
      preview.style.position = "absolute";
      preview.style.top = "-1000px"; // Keep it off-screen
      preview.style.pointerEvents = "none";
      preview.style.background = "rgba(0,0,0,0.8)";
      preview.style.padding = "8px 12px";
      preview.style.borderRadius = "4px";
      preview.style.display = "inline-block";
      preview.style.color = "white";
      preview.style.fontSize = "14px";
      preview.style.fontWeight = "bold";
      preview.style.zIndex = "9999";
      document.body.appendChild(preview);
    }

    dragPreviewRef.current = preview;
  }, []);

  // Handle listening to any element being dragged
  useEffect(() => {
    const handleDragStart = (e: DragEvent) => {
      if (e.target instanceof HTMLElement && e.target.dataset.dragId === dragId)
        setIsDraggingAnEl(true);
    };
    const removeDragging = () => {
      setIsDraggingAnEl(false);
      setIsDraggingThisEl(false);
    };

    document.addEventListener("dragstart", handleDragStart);
    document.addEventListener("dragend", removeDragging);
    document.addEventListener("drop", removeDragging);

    return () => {
      document.removeEventListener("dragstart", handleDragStart);
      document.removeEventListener("dragend", removeDragging);
      document.addEventListener("drop", removeDragging);
    };
  }, [dragId]);

  return {
    isDraggingThisEl,
    isDraggingAnEl,
    dropProps,
    dragProps,
  };
}
